<template>
  <v-dialog
    :value="value"
    persistent
    max-width="480"
    @input="$emit('input', $event)"
  >
    <v-card>
      <div class="modal-container">
        <div class="modal-header">
          <div class="info-icon bg-warning/45">
            <v-icon color="warning" size="24">
              mdi-alert-circle-outline
            </v-icon>
          </div>

          <div class="header-headline">
            คุณมีรายการที่ยังไม่ชำระเงินค้างอยู่
          </div>

          <div class="header-desc">
            คุณสามารถเลือกที่จะไปยังรายการสั่งซื้อเดิมเพื่อชำระเงินต่อ
            หรือยกเลิกรายการเดิมและสร้างรายการใหม่
          </div>

          <ul v-if="pendingOrders.length" class="order-pending-list">
            <li
              v-for="(order, index) in pendingOrders"
              :key="order.ref_id || index"
              class="order-pending-list-item"
            >
              <div class="item-content">
                <div class="item-ref-row">
                  <div class="item-ref">
                    {{ order.ref_id }}
                  </div>
                  <OrderDetailStatusBadge
                    :status="ORDER_STATUS.CREATE"
                  />
                </div>
                <div v-if="order.product_name" class="item-product">
                  {{ order.product_name }}
                </div>
                <div
                  v-if="order.created_at || otherBuLabel(order)"
                  class="item-date"
                >
                  <template v-if="order.created_at">
                    สั่งซื้อเมื่อ
                    {{ formatCreatedAt(order.created_at) }}
                  </template>
                  <template v-if="otherBuLabel(order)">
                    <template v-if="order.created_at"> • </template>
                    สั่งซื้อผ่าน {{ otherBuLabel(order) }}
                  </template>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <div class="modal-footer">
          <v-btn
            depressed
            rounded
            block
            large
            color="primary"
            style="text-transform: none"
            :disabled="loading || !firstOrder.uuid"
            @click="onViewOrder"
          >
            ไปยังรายการสั่งซื้อเดิม
            <span v-if="otherBuLabel(firstOrder)" class="ml-1">
              {{ otherBuLabel(firstOrder) }}
            </span>
          </v-btn>

          <v-btn
            outlined
            depressed
            rounded
            block
            large
            dark
            color="error"
            :loading="loading"
            @click="onConfirm"
          >
            ยกเลิกคำสั่งซื้อเดิมและสร้างใหม่
          </v-btn>

          <v-btn
            text
            rounded
            block
            color="primary"
            :disabled="loading"
            @click="onClose"
          >
            ปิด
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import { ORDER_STATUS } from '@/config/order'
import { BU_TYPE, currentBu } from '@/config/buDomain'

const BU_LABEL = {
  [BU_TYPE.STUDIO7]: 'Studio7',
  [BU_TYPE.BNN]: 'BaNANA',
}

export default {
  name: 'CheckoutModalPendingOrder',

  components: {
    OrderDetailStatusBadge: () =>
      import('@/components/order/OrderDetailStatusBadge.vue'),
  },

  data() {
    return {
      ORDER_STATUS,
    }
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    pendingOrders: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    // The first pending order: uuid is nested under `order`, `type` is its BU
    firstOrder() {
      const first = this.pendingOrders?.[0]

      return {
        uuid: first?.order?.uuid ?? first?.uuid ?? null,
        type: this.orderType(first),
      }
    },
  },

  methods: {
    orderType(order) {
      return String(
        order?.type ?? order?.order?.type ?? '',
      ).toUpperCase()
    },

    // Name of the BU the order was placed on, only when it is the other BU
    otherBuLabel(order) {
      const type = this.orderType(order)

      return type && type !== currentBu ? BU_LABEL[type] || type : ''
    },

    formatCreatedAt(value) {
      const date = moment(value)

      return date.isValid()
        ? date.locale('th').format('D MMM YYYY HH:mm:ss')
        : value
    },

    onClose() {
      this.$emit('input', false)
    },

    onViewOrder() {
      const { uuid, type } = this.firstOrder
      if (!uuid) return

      this.$emit('input', false)
      this.$emit('view-order', uuid, type)
    },

    onConfirm() {
      this.$emit('confirm')
    },
  },
}
</script>

<style lang="stylus" scoped>
.modal-container {
    position: relative;
    overflow: auto;
    max-height: 75vh;
}

.modal-header {
    padding: 20px 20px 16px;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .info-icon {
        display: flex;
        align-items: center;
        justify-content: center;

        width: 48px;
        height: 48px;
        border-radius: 50%;

		background-color: rgba($color-warning, 0.15);
    }

    .header-headline {
        font-size: 18px;
        font-weight: 700;
        color: rgba(#000000, 0.85);
        text-align: center;
    }

    .header-desc {
        font-size: 14px;
        color: rgba(#000000, 0.65);
        text-align: center;
    }

    .order-pending-list {
        width: 100%;
        margin: 0;
        padding: 0;
        list-style: none;
        font-size: 14px;
        color: rgba(#000000, 0.85);
        text-align: left;

        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .order-pending-list-item {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px;

        border: 1px solid $color-bnn-border-btn;
        border-radius: 12px;

        .item-content {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        .item-product {
            font-weight: 500;
            display: -webkit-box;
            overflow: hidden;
            text-overflow: ellipsis;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
        }

        .item-date {
            font-size: 12px;
            color: rgba(#000000, 0.55);
        }

        .item-ref-row {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 4px 8px;
        }

        .item-ref {
            font-weight: 700;
            word-break: break-all;
			text-transform: uppercase;
        }
    }
}

.modal-footer {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 8px 20px 20px;
}
</style>
